import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

// P2 AUTOSAVE — draft persistence state machine.
// PRISTINE → DIRTY → SAVING → SAVED / SAVE_FAILED / CONFLICT
// - Clinical content lives in memory + on the server draft only.
// - localStorage stores ONLY a small pointer (flag + revision + timestamp) —
//   never clinical text. LGPD-safe A+C minimal strategy. This lets the next
//   mount warn the user if the previous session ended with unsynced changes.
// - Never emits audit events (high-frequency ticks stay out of the trail).
const IDLE_DEBOUNCE_MS = 2000;
const MAX_INTERVAL_MS = 15000;
const RETRY_DELAYS_MS = [2000, 5000, 15000];

// Small pointer stored on same origin. NO clinical content.
const flagKey = (id) => `akali:draft-flag:${id}`;

const readFlag = (id) => {
  try {
    const raw = localStorage.getItem(flagKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeFlag = (id, patch) => {
  try {
    const current = readFlag(id) || {};
    const next = { ...current, ...patch };
    localStorage.setItem(flagKey(id), JSON.stringify(next));
  } catch {
    /* localStorage unavailable — offline warning path still works via memory. */
  }
};

const clearFlag = (id) => {
  try { localStorage.removeItem(flagKey(id)); } catch { /* ignore */ }
};

export const useDraftAutosave = ({ consultationId, enabled, onDeleted, onLoadServer }) => {
  const [status, setStatus] = useState('pristine');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  );

  const docRef = useRef(null);
  const dirtyRef = useRef(false);
  const revRef = useRef(0);
  const savingRef = useRef(false);
  const conflictRef = useRef(null);
  const retryCount = useRef(0);
  const idleTimer = useRef(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onDeletedRef = useRef(onDeleted);
  onDeletedRef.current = onDeleted;
  const onLoadServerRef = useRef(onLoadServer);
  onLoadServerRef.current = onLoadServer;

  // A+C pointer — surface previous-session "unsynced" flag only once.
  const [unsyncedFromPreviousSession, setUnsyncedFromPreviousSession] = useState(false);
  useEffect(() => {
    if (!consultationId) return;
    const flag = readFlag(consultationId);
    if (flag && flag.dirtyFlag) {
      setUnsyncedFromPreviousSession(true);
    }
    // Auto-clear if not dirty (defensive).
    if (flag && !flag.dirtyFlag) clearFlag(consultationId);
  }, [consultationId]);

  const acknowledgeUnsyncedWarning = useCallback(() => {
    setUnsyncedFromPreviousSession(false);
    if (consultationId) clearFlag(consultationId);
  }, [consultationId]);

  const doSave = useCallback(async () => {
    if (!enabledRef.current || !dirtyRef.current || savingRef.current || conflictRef.current) return false;
    savingRef.current = true;
    setStatus('saving');
    const payloadDoc = docRef.current;
    try {
      const res = await api.saveDraftContent(consultationId, revRef.current, payloadDoc);
      revRef.current = res.draftRevision;
      retryCount.current = 0;
      if (docRef.current === payloadDoc) {
        dirtyRef.current = false;
        setStatus('saved');
        clearFlag(consultationId);
      } else {
        setStatus('dirty');
        writeFlag(consultationId, {
          dirtyFlag: true,
          lastRevision: revRef.current,
          lastAttemptAt: new Date().toISOString(),
        });
        clearTimeout(idleTimer.current);
        idleTimer.current = setTimeout(() => doSave(), IDLE_DEBOUNCE_MS);
      }
      setLastSavedAt(res.draftUpdatedAt || new Date().toISOString());
      return true;
    } catch (err) {
      const httpStatus = err?.response?.status;
      if (httpStatus === 409) {
        conflictRef.current = err.response.data;
        setConflict(err.response.data);
        setStatus('conflict');
        writeFlag(consultationId, {
          dirtyFlag: true,
          lastRevision: revRef.current,
          lastAttemptAt: new Date().toISOString(),
        });
        return false;
      }
      if (httpStatus === 410) {
        dirtyRef.current = false;
        setStatus('pristine');
        clearFlag(consultationId);
        onDeletedRef.current?.();
        return false;
      }
      if (httpStatus === 422) {
        dirtyRef.current = false;
        setStatus('pristine');
        clearFlag(consultationId);
        return false;
      }
      const attempt = retryCount.current;
      if (attempt < RETRY_DELAYS_MS.length) {
        retryCount.current = attempt + 1;
        writeFlag(consultationId, {
          dirtyFlag: true,
          lastRevision: revRef.current,
          lastAttemptAt: new Date().toISOString(),
        });
        setTimeout(() => doSave(), RETRY_DELAYS_MS[attempt]);
        return false;
      }
      setStatus('failed');
      writeFlag(consultationId, {
        dirtyFlag: true,
        lastRevision: revRef.current,
        lastAttemptAt: new Date().toISOString(),
      });
      return false;
    } finally {
      savingRef.current = false;
    }
  }, [consultationId]);

  const markDirty = useCallback((doc) => {
    if (!enabledRef.current) return;
    docRef.current = doc;
    dirtyRef.current = true;
    retryCount.current = 0;
    writeFlag(consultationId, {
      dirtyFlag: true,
      lastRevision: revRef.current,
      lastAttemptAt: new Date().toISOString(),
    });
    if (conflictRef.current) return; // blocked until the conflict is resolved
    setStatus('dirty');
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => doSave(), IDLE_DEBOUNCE_MS);
  }, [doSave, consultationId]);

  const flush = useCallback(async () => {
    clearTimeout(idleTimer.current);
    if (!dirtyRef.current) return true;
    return doSave();
  }, [doSave]);

  const setRevision = useCallback((rev) => {
    revRef.current = Number(rev) || 0;
  }, []);

  const resolveConflict = useCallback((mode) => {
    const server = conflictRef.current;
    if (!server) return;
    conflictRef.current = null;
    setConflict(null);
    revRef.current = Number(server.draftRevision) || 0;
    if (mode === 'keepMine') {
      dirtyRef.current = true;
      retryCount.current = 0;
      setStatus('dirty');
      doSave();
    } else {
      onLoadServerRef.current?.(server);
      dirtyRef.current = false;
      setStatus('saved');
      clearFlag(consultationId);
      if (server.draftUpdatedAt) setLastSavedAt(server.draftUpdatedAt);
    }
  }, [doSave, consultationId]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') doSave();
    };
    const onBlur = () => doSave();
    const onBeforeUnload = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    const onOnline = () => {
      setIsOffline(false);
      if (dirtyRef.current) {
        retryCount.current = 0;
        doSave();
      }
    };
    const onOffline = () => setIsOffline(true);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const interval = setInterval(() => {
      if (dirtyRef.current) doSave();
    }, MAX_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(interval);
      clearTimeout(idleTimer.current);
    };
  }, [enabled, doSave]);

  return {
    status,
    lastSavedAt,
    conflict,
    isOffline,
    unsyncedFromPreviousSession,
    acknowledgeUnsyncedWarning,
    markDirty,
    flush,
    setRevision,
    resolveConflict,
  };
};
